import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../i18n';
import SearchBar from '../components/SearchBar';
import { searchWeb } from '../services/searchService';
import './SearchResults.css';

const SearchResults = () => {
  const { t, language } = useTranslation();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [error, setError] = useState(null);
  const [searchTime, setSearchTime] = useState(0);
  const [totalResults, setTotalResults] = useState(0);

  const business = process.env.REACT_APP_SITE_NAME;
  const first_name = business ? business.split(' ')[0] : 'Seek';
  const last_name = business ? business.split(' ').slice(1).join(' ') : 'Webs';

  const localeMap = {
    pt: 'pt-BR',
    en: 'en-US',
    es: 'es-ES'
  };

  const tabs = [
    { id: 'all', label: t('search.tabs.all'), icon: '🔍' },
  ];

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    setError(null);

    try {
      const searchResults = await searchWeb(searchQuery);

      if (searchResults.results && searchResults.results.length > 0) {
        setResults(searchResults.results);
        setSearchTime(searchResults.searchTime);
        setTotalResults(searchResults.totalResults);
      } else {
        setResults([]);
        setError(t('search.noResults'));
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(t('search.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const ResultCard = ({ result, index }) => (
    <motion.article
      className="result-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ x: 4 }}
    >
      <div className="result-header">
        <img
          className="result-favicon"
          src={result.favicon}
          alt=""
          width="16"
          height="16"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <cite className="result-url">{new URL(result.url).hostname}</cite>
      </div>
      <h2 className="result-title">
        <a href={result.url} target="_blank" rel="noopener noreferrer">
          {result.title}
        </a>
      </h2>
      <p className="result-description">{result.description}</p>
      <div className="result-actions">
        <button className="action-button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8.5 2.5L10.5 6.5L15 7L11.5 10.5L12.5 15L8.5 12.5L4.5 15L5.5 10.5L2 7L6.5 6.5L8.5 2.5Z"
              stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
          {t('common.save')}
        </button>
        <button className="action-button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M14 10V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V10M11 7L8 10M8 10L5 7M8 10V2"
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {t('common.share')}
        </button>
      </div>
    </motion.article>
  );

  return (
    <>
      <Helmet>
        <title>{query} - {process.env.REACT_APP_BUSINESS}</title>
        <meta name="description" content={`${t('search.resultsInfo', { total: totalResults, time: searchTime })}`} />
      </Helmet>

      <div className="search-results-container">
        <header className="search-header">
          <Link to="/" className="logo-link">
            <div className="mini-logo">
              <span className="logo-text">{first_name}</span>
              <span className="logo-accent">{last_name}</span>
            </div>
          </Link>
          <div className="search-bar-wrapper">
            <SearchBar isCompact initialQuery={query} />
          </div>
        </header>

        <nav className="search-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <main className="search-main">
          {loading ? (
            <div className="loading-container">
              <motion.div
                className="loading-spinner"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              <p>{t('search.searching')}</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <h2>{t('search.errorTitle')}</h2>
              <p>{error}</p>
              <button onClick={() => performSearch(query)} className="retry-button">
                {t('common.tryAgain')}
              </button>
            </div>
          ) : (
            <>
              <div className="results-info">
                <p>{t('search.resultsInfo', { total: totalResults.toLocaleString(localeMap[language] || 'pt-BR'), time: searchTime })}</p>
              </div>

              <AnimatePresence>
                <div className="results-list">
                  {results.map((result, index) => (
                    <ResultCard key={result.id} result={result} index={index} />
                  ))}
                </div>
              </AnimatePresence>

              {results.length > 0 && (
                <div className="pagination">
                  <button className="page-button prev" disabled>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('common.previous')}
                  </button>
                  <div className="page-numbers">
                    <button className="page-number active">1</button>
                    <button className="page-number">2</button>
                    <button className="page-number">3</button>
                    <span className="page-dots">...</span>
                    <button className="page-number">10</button>
                  </div>
                  <button className="page-button next">
                    {t('common.next')}
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </main>

        <aside className="search-sidebar">
          <div className="related-searches">
            <h3>{t('search.relatedSearches')}</h3>
            <ul>
              <li><a href={`/search?q=${encodeURIComponent(query + ' tutorial')}`}>{t('search.suggestions.tutorial', { query })}</a></li>
              <li><a href={`/search?q=${encodeURIComponent(query + ' examples')}`}>{t('search.suggestions.examples', { query })}</a></li>
              <li><a href={`/search?q=${encodeURIComponent(query + ' documentation')}`}>{t('search.suggestions.documentation', { query })}</a></li>
              <li><a href={`/search?q=${encodeURIComponent(query + ' vs alternatives')}`}>{t('search.suggestions.vsAlternatives', { query })}</a></li>
            </ul>
          </div>
        </aside>
      </div>
    </>
  );
};

export default SearchResults;
